/* ============================================================
 * 60ft Ink — Self-serve quote calculator
 * ------------------------------------------------------------
 * Fast, visual "what would this cost" estimate shown above the
 * real print-location picker/size-quantity matrix, so a customer
 * can see roughly where they land before committing to the full
 * flow. Not a replacement for size-quantity-matrix.liquid (the
 * actual order builder) — a quick teaser sitting alongside it.
 *
 * Sources real numbers rather than hardcoding a second copy:
 *   - Print location prices are read directly off the already-
 *     rendered print-location-selector.liquid markup on the same
 *     page (.pls-option[data-location][data-price]) — zero drift
 *     risk if those prices ever change, since there's nothing to
 *     keep in sync manually.
 *   - Garment price comes from product.price_min, written into a
 *     data attribute by the Liquid snippet at render time.
 *   - Bulk-quantity discount tiers are NOT readable the same way
 *     (size-quantity-matrix.liquid keeps its own `discountTiers`
 *     array private inside its own IIFE, not exposed on window),
 *     so they're duplicated here as a literal array. Must be kept
 *     in sync by hand with size-quantity-matrix.liquid's own
 *     `discountTiers` if that ever changes — same accepted
 *     duplicated-constant pattern already used elsewhere in this
 *     project (the 3 print-addon variant IDs are independently
 *     duplicated across cart-item.liquid/theme.js/size-quantity-
 *     matrix.liquid too).
 * ============================================================ */

(function () {
  'use strict';

  // Real, enforced bulk ladder as of 2026-08-26 — mirrors the Hulk
  // Discounts tier tables (see assets/ai-designer.js's DEFAULTS.tierTable
  // for the full rationale). Was previously the old 5/20-10/25-20/30-50/35-
  // 100/40-200/50 ladder, which Hulk itself no longer enforces.
  var DISCOUNT_TIERS = [
    { min: 200, pct: 35 },
    { min: 100, pct: 33 },
    { min: 50, pct: 30 },
    { min: 20, pct: 25 },
    { min: 10, pct: 18 },
    { min: 1, pct: 0 }
  ];

  var LOCATION_LABELS = {
    front: 'Left chest',
    large_front: 'Large front',
    back: 'Large back'
  };

  var MUTUALLY_EXCLUSIVE = ['front', 'large_front'];

  function money(n) {
    return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getDiscount(qty) {
    for (var i = 0; i < DISCOUNT_TIERS.length; i++) {
      if (qty >= DISCOUNT_TIERS[i].min) return DISCOUNT_TIERS[i];
    }
    return DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1];
  }

  function readLocations(root) {
    var pageOptions = document.querySelectorAll('.pls-option[data-location][data-price]');
    var locations = [];
    pageOptions.forEach(function (el) {
      var location = el.getAttribute('data-location');
      var price = parseFloat(el.getAttribute('data-price'));
      if (!location || isNaN(price)) return;
      locations.push({
        location: location,
        price: price,
        label: LOCATION_LABELS[location] || location
      });
    });
    return locations;
  }

  function initCalculator(root) {
    var basePrice = parseFloat(root.getAttribute('data-base-price'));
    var priceVaries = root.getAttribute('data-price-varies') === 'true';
    if (isNaN(basePrice)) return;

    var locations = readLocations(root);
    if (!locations.length) return;

    var locWrap = root.querySelector('[data-qc-locations]');
    var qtyInput = root.querySelector('[data-qc-qty]');
    var qtyDisplay = root.querySelector('[data-qc-qty-display]');
    var unitOut = root.querySelector('[data-qc-unit]');
    var totalOut = root.querySelector('[data-qc-total]');
    var tableBody = root.querySelector('[data-qc-table]');

    locations.forEach(function (loc, i) {
      var wrapper = document.createElement('label');
      wrapper.className = 'ft-qc__loc';
      var input = document.createElement('input');
      input.type = 'checkbox';
      input.dataset.location = loc.location;
      input.dataset.price = loc.price;
      if (i === 0) input.checked = true;
      wrapper.appendChild(input);
      wrapper.appendChild(document.createTextNode(loc.label + ' (+' + money(loc.price) + ')'));
      locWrap.appendChild(wrapper);
    });

    var locInputs = locWrap.querySelectorAll('input[type="checkbox"]');

    locInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked && MUTUALLY_EXCLUSIVE.indexOf(input.dataset.location) !== -1) {
          locInputs.forEach(function (other) {
            if (other !== input && MUTUALLY_EXCLUSIVE.indexOf(other.dataset.location) !== -1) {
              other.checked = false;
            }
          });
        }
        render();
      });
    });

    function selectedLocationFee() {
      var fee = 0;
      locInputs.forEach(function (input) { if (input.checked) fee += parseFloat(input.dataset.price); });
      return fee;
    }

    function render() {
      var qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      var tier = getDiscount(qty);
      var garmentUnit = basePrice * (1 - tier.pct / 100);
      var unit = garmentUnit + selectedLocationFee();
      var total = unit * qty;

      qtyDisplay.textContent = qty;
      unitOut.textContent = (priceVaries ? 'from ' : '') + money(unit);
      totalOut.textContent = money(total);

      tableBody.innerHTML = '';
      DISCOUNT_TIERS.slice().reverse().forEach(function (t) {
        var isActive = t === tier;
        var rowUnit = basePrice * (1 - t.pct / 100) + selectedLocationFee();
        var tr = document.createElement('tr');
        if (isActive) tr.className = 'is-active';
        var qtyLabel = t.min === 1 ? '1+' : t.min + '+';
        tr.innerHTML =
          '<td>' + qtyLabel + '</td>' +
          '<td>' + t.pct + '%</td>' +
          '<td>' + (priceVaries ? 'from ' : '') + money(rowUnit) + '</td>';
        tableBody.appendChild(tr);
      });
    }

    qtyInput.addEventListener('input', render);
    render();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-ft-quote-calculator]').forEach(initCalculator);
  });
})();
